import { useEffect, useState } from "react";

// STYLES
import * as Style from "./AddSupportItemStyle";

// LIBRARIES
import { useTranslation } from "react-i18next";

// MISC
import { ButtonTypes, InputTypes, SnackbarTypes } from "models/GeneralTypes/generalTypes";
import { IAddArticle, IAddSubcategory, ICategory, IGetParentChildren, ISubcategory } from "models/interfaces";
import { IModalData } from "../../ModalAtomModel";
import { IProps } from "./AddSupportItemModel";

// ASSETS
import EditIconHovered from "assets/Icons/HistoryIconHovered.svg";

// REDUX
import { createArticle, createCategory, createSubcategory, getAllCategories, getCategorySubcategories, getSubcategoryArticles } from "api/supportApi";
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";
import { language } from "slices/sessionSlice";
import { categories, subcategories } from "slices/supportSlice";
import { modalProps, toggleModalState, toggleSnackbarState } from "slices/uiSlice";

// COMPONENTS
import ButtonAtom from "components/Atoms/ButtonAtom/ButtonAtom";
import InputAtom from "components/Atoms/InputAtom/InputAtom";
import TextEditor from "components/TextEditor/TextEditor";

const AddSupportItem = (props: IProps) => {
  // PROPS
  const { handleClose = () => { } } = props;

  // CONSTANTS USING LIBRARIES
  const dispatch = useAppDispatch();
  const modalData = useAppSelector<IModalData>(modalProps);
  const selectedLanguage = useAppSelector<string>(language);
  const allCategories = useAppSelector<ICategory[]>(categories);
  const categorySubcategories = useAppSelector<ISubcategory[]>(subcategories);
  const { t } = useTranslation();

  // CONSTANTS USING HOOKS
  const [titleInputValue, setTitleInputValue] = useState<string>("");
  const [articleContent, setArticleContent] = useState<string>("");
  const [type, setType] = useState<string>("category");
  const [categoryTitle, setCategoryTitle] = useState<string>("")
  const [subcategoryTitle, setSubcategoryTitle] = useState<string>("")

  // GENERAL CONSTANTS

  // USE EFFECT FUNCTION
  useEffect(() => {
    dispatch(getAllCategories(selectedLanguage)).catch((error) => {
      console.log(error);
    });
    // eslint-disable-next-line
  }, []);

  // REQUEST API FUNCTIONS

  // HANDLERS FUNCTIONS
  const handleToggleTypeValue = (type) => {
    setType(type);
  }

  const handleSelectCategory = (category: ICategory) => {
    let payload: IGetParentChildren = {
      id: category.id,
      language: selectedLanguage,
    };
    console.log({ payload })
    dispatch(getCategorySubcategories(payload))
      .catch((error) => {
        console.log(error);
      })
      .then(() => {
        dispatch(
          toggleModalState({
            ...modalData,
            category: category,
            subcategory: null,
            article: null,
          })
        );
      })
      .then(() => {
        dispatch(getSubcategoryArticles(null));
      });
  };

  const handleSelectSubcategory = (subcategory: ISubcategory) => {
    let payload: IGetParentChildren = {
      id: subcategory.id,
      language: selectedLanguage,
    };
    dispatch(getSubcategoryArticles(payload))
      .catch((error) => {
        console.log(error);
      })
      .then(() => {
        dispatch(
          toggleModalState({
            ...modalData,
            subcategory: subcategory,
            article: null,
          })
        );
      });
  };

  const handleAddItem = () => {
    if (type === "category") {
      dispatch(createCategory(categoryTitle))
        .then(() => {
          dispatch(getAllCategories(selectedLanguage))
            .then(() => {
              dispatch(
                toggleModalState({
                  ...modalData,
                  isVisible: false,
                  type: "",
                })
              );
              dispatch(toggleSnackbarState({
                isVisible: true,
                type: SnackbarTypes.SUPPORT_SAVE_SUCCESS,
                item: t("snackbar.category"),
              }))
            });
        })
        .catch((error) => {
          console.log(error)
          dispatch(toggleSnackbarState({
            isVisible: true,
            type: SnackbarTypes.SUPPORT_ERROR,
          }))
        })
    };
    if (type === "subcategory") {
      let payload: IAddSubcategory = {
        id: modalData.category.id,
        title: subcategoryTitle
      };
      dispatch(createSubcategory(payload))
        .then(() => {
          let payload: IGetParentChildren = {
            id: modalData.category.id,
            language: selectedLanguage
          };
          dispatch(getCategorySubcategories(payload))
            .then(() => {
              dispatch(
                toggleModalState({
                  ...modalData,
                  isVisible: false,
                  type: "",
                })
              );
              dispatch(toggleSnackbarState({
                isVisible: true,
                type: SnackbarTypes.SUPPORT_SAVE_SUCCESS,
                item: t("snackbar.subcategory"),
              }))
            });
        })
        .catch((error) => {
          console.log(error)
          dispatch(toggleSnackbarState({
            isVisible: true,
            type: SnackbarTypes.SUPPORT_ERROR,
          }))
        })
    };
    if (type === "article") {
      let payload: IAddArticle = {
        content: articleContent,
        subcategoryId: modalData.subcategory.id,
        title: titleInputValue,
      };
      dispatch(createArticle(payload))
        .then(() => {
          let payload: IGetParentChildren = {
            id: modalData.subcategory.id,
            language: selectedLanguage
          };
          dispatch(getSubcategoryArticles(payload))
            .then(() => {
              dispatch(
                toggleModalState({
                  ...modalData,
                  isVisible: false,
                  type: "",
                })
              );
              dispatch(toggleSnackbarState({
                isVisible: true,
                type: SnackbarTypes.SUPPORT_SAVE_SUCCESS,
                item: t("snackbar.article"),
              }))
            });
        })
        .catch((error) => {
          console.log(error)
          dispatch(toggleSnackbarState({
            isVisible: true,
            type: SnackbarTypes.SUPPORT_ERROR,
          }))
        })
    };
  };

  return (
    <Style.ModalContainer>
      <Style.CloseIconContainer>
        <Style.CloseIcon onClick={handleClose} />
      </Style.CloseIconContainer>
      {/* <Style.Title color={COLORS.terraCotta}>
        {`${t(`support.add`)} ${t(`support.${type}`)}`}
      </Style.Title> */}
      <Style.HeaderWrapper>
        <Style.IconContainer>
          <Style.HeaderIcon
            src={EditIconHovered}

          >
          </Style.HeaderIcon>
        </Style.IconContainer>
        <Style.ContentInfo>
          <Style.HeaderTitle>
          {`${t(`support.add`)} ${t(`support.${type}`)}`}
          </Style.HeaderTitle>
          <Style.HeaderSubtitle>
          {`${t(`support.add`)} ${t(`support.${type}`)}`}
          </Style.HeaderSubtitle>
        </Style.ContentInfo>
      </Style.HeaderWrapper>
      <Style.ContentWrapper>
        <Style.Section>
          <Style.Subtitle>
            {t("support.selectCategoryType")}
          </Style.Subtitle>
          <Style.SectionContent>
            <Style.Line onClick={() => handleToggleTypeValue("category")}>
              <Style.CheckBox isSelected={type === "category"} />
              <Style.Value>
                {t("support.category")}
              </Style.Value>
            </Style.Line>
            <Style.Line onClick={() => handleToggleTypeValue("subcategory")}>
              <Style.CheckBox isSelected={type === "subcategory"} />
              <Style.Value>
                {t("support.subcategory")}
              </Style.Value>
            </Style.Line>
            <Style.Line onClick={() => handleToggleTypeValue("article")}>
              <Style.CheckBox isSelected={type === "article"} />
              <Style.Value>
                {t("support.article")}
              </Style.Value>
            </Style.Line>
          </Style.SectionContent>
        </Style.Section>
        {type !== "category" && <Style.Section>
          <Style.Subtitle>
            {t("support.selectCategory")}
          </Style.Subtitle>
          <Style.SectionContent>
            {allCategories.map((item, index) => (
              <Style.Line onClick={() => handleSelectCategory(item)}>
                <Style.CheckBox isSelected={item?.id === modalData?.category?.id} />
                <Style.Value>
                  {item.name || ""}
                </Style.Value>
              </Style.Line>
            ))}

          </Style.SectionContent>
        </Style.Section>}
        {type === "article" && <Style.Section>
          <Style.Subtitle>
            {t("support.selectSubcategory")}
          </Style.Subtitle>
          <Style.SectionContent>
            {categorySubcategories.map((item, index) => (
              <Style.Line onClick={() => handleSelectSubcategory(item)}>
                <Style.CheckBox isSelected={item?.id === modalData?.subcategory?.id} />
                <Style.Value>
                  {item.name || ""}
                </Style.Value>
              </Style.Line>
            ))}
          </Style.SectionContent>
        </Style.Section>}
        <Style.Section>
          <Style.Subtitle>
            {t("support.addInfo")}
          </Style.Subtitle>
          <Style.SectionContent>
            {type === "category" && <Style.InputAtomContainer>
              <InputAtom
                // name="date"
                type={InputTypes.MODAL_INPUT}
                placeholder="Scrie..."
                label="Category name"
                inputValue={categoryTitle}
                setInputValue={setCategoryTitle}
              />
            </Style.InputAtomContainer>}
            {type === "subcategory" && <Style.InputAtomContainer>
              <InputAtom
                // name="date"
                type={InputTypes.MODAL_INPUT}
                placeholder="Scrie..."
                label="Subcategory name"
                hasLabel={true}
                inputValue={subcategoryTitle}
                setInputValue={setSubcategoryTitle}
              />
            </Style.InputAtomContainer>}
            {type === "article" &&
              <>
                <Style.InputAtomContainer>
                  <InputAtom
                    type={InputTypes.MODAL_INPUT}
                    placeholder="Scrie..."
                    label="Article name"
                    hasLabel={true}
                    inputValue={titleInputValue}
                    setInputValue={setTitleInputValue}
                  />
                </Style.InputAtomContainer>
                <Style.InputAtomContainer>
                  <TextEditor value={articleContent} label="Description" setValue={setArticleContent} height={350} />
                </Style.InputAtomContainer>
              </>
            }
          </Style.SectionContent>
        </Style.Section>
        <Style.ButtonContainer>
          <ButtonAtom
            text={t("support.save")}
            type={ButtonTypes.MAIN_BUTTON}
            onClick={() => handleAddItem()}
          />
        </Style.ButtonContainer>

      </Style.ContentWrapper>
    </Style.ModalContainer>
  );
};

export default AddSupportItem;
